import { TableRow, TableRowProps } from "@mui/material";
import { type MouseEvent, ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import ContextMenu from "../contextMenu/ContextMenu";
import { useContextMenu } from "../contextMenu/useContextMenu";

type Props = {
  to: string;
  children: ReactElement | ReactElement[];
  TableRowProps?: TableRowProps;
  testId?: string;
};

const isTextSelection = (): boolean => {
  const sel = window.getSelection?.();
  // user has selected text
  return !!sel && sel.type === "Range" && (sel.toString()?.length ?? 0) > 0;
};

const TableLinkRow = ({
  to,
  children,
  TableRowProps = {},
  testId,
}: Props) => {
  const navigate = useNavigate();
  const { contextMenuProps, openContextMenu, closeContextMenu } =
    useContextMenu();

  const handleClick = (e: MouseEvent<HTMLTableRowElement>) => {
    // Don't navigate if text is selected
    if (isTextSelection()) return;

    // Don't navigate if the default has been prevented (e.g., by browser context menu)
    if (e.defaultPrevented) return;

    // Respect modifier keys: open in new tab/window if user asks
    if (e.metaKey || e.ctrlKey) {
      window.open(to, "_blank", "noopener,noreferrer");
      return;
    }

    // Only navigate on left click (button 0)
    if (e.button === 0) {
      navigate(to);
    }
  };

  const handleMouseUp = (e: MouseEvent<HTMLTableRowElement>) => {
    if (isTextSelection()) return;

    // Middle mouse button (wheel click) - open in new tab
    if (e.button === 1) {
      e.preventDefault();
      window.open(to, "_blank", "noopener,noreferrer");
      return;
    }
  };

  const handleOpenInNewTab = () => {
    window.open(to, "_blank", "noopener,noreferrer");
    closeContextMenu();
  };

  return (
    <>
      <TableRow
        onClick={handleClick}
        onMouseUp={handleMouseUp}
        onContextMenu={openContextMenu}
        style={{
          textDecoration: "none",
          color: "inherit",
        }}
        {...(testId ? { "data-testid": testId } : {})}
        {...TableRowProps}
        sx={{
          userSelect: "text",
          textDecoration: "none",
          color: "inherit",
          cursor: "pointer",
          "&:hover": { backgroundColor: (t) => t.palette.action.hover },
          "& td": {
            userSelect: "text",
          },
          ...TableRowProps?.sx,
        }}
      >
        {children}
      </TableRow>
      <ContextMenu
        {...contextMenuProps}
        onOpenInNewTab={handleOpenInNewTab}
        url={window.location.origin + to}
      />
    </>
  );
};

export default TableLinkRow;
